import React, { useEffect, useState } from "react";
import { fetchTipsLeaderboard } from "../lib/api";
import { useFeatureFlags } from "../context/FeatureFlagsContext";
import { AvatarChip } from "./AvatarChip";

/**
 * TipsLeaderboard — viral growth loop for the creator-payouts feature.
 *
 * Why it exists
 * -------------
 * People love to see who's getting paid. Surface the top tipped
 * players this week and you get two compounding effects:
 *   • Tippers feel social proof — "everyone is tipping, I should too".
 *   • Tipped players feel public recognition — they share their
 *     position on social, driving organic reach.
 *
 * Gating
 * ------
 * Hidden entirely until the global `tips_enabled` flag is on. The
 * backend mirrors this gate (returns ``{"enabled": false}`` so even
 * curl users see nothing). When the flag flips off mid-session, the
 * component yanks itself from the DOM on the next refresh poll.
 */
export function TipsLeaderboard({ window: defaultWindow = "week", limit = 5 }) {
  const { flags } = useFeatureFlags();
  const [window, setWindow] = useState(defaultWindow);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    if (!flags?.tips_enabled) { setData(null); setLoading(false); return; }
    setLoading(true);
    fetchTipsLeaderboard(window, limit)
      .then((d) => { if (!cancelled) setData(d); })
      .catch(() => { if (!cancelled) setData(null); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [flags?.tips_enabled, window, limit]);

  if (!flags?.tips_enabled) return null;
  if (loading) return null;
  if (!data?.enabled) return null;

  const rows = data.rows || [];
  if (rows.length === 0) return null;  // empty board hidden — looks bad otherwise

  return (
    <section
      data-testid="tips-leaderboard"
      className="rounded-2xl border border-[#14b8a6]/30 bg-gradient-to-br from-[#0a1f1c] via-[#0b2823] to-[#0a1f1c] p-5"
    >
      <header className="flex items-center justify-between mb-3">
        <div>
          <p className="text-[10px] tracking-[4px] text-[#14b8a6] font-bold">
            <i className="fas fa-fire mr-1" />TOP TIPPED OPERATORS
          </p>
          <p className="text-[11px] text-white/45 mt-0.5">
            Players the community is paying right now.
          </p>
        </div>
        <div className="flex gap-1">
          {["week", "month", "alltime"].map((w) => (
            <button
              key={w}
              type="button"
              onClick={() => setWindow(w)}
              data-testid={`tips-lb-window-${w}`}
              className={`text-[10px] tracking-widest px-2.5 py-1 rounded-lg ${
                window === w
                  ? "bg-[#14b8a6]/20 text-[#14b8a6] border border-[#14b8a6]/40"
                  : "bg-black/30 text-white/50 border border-white/10"
              }`}
            >
              {w === "alltime" ? "ALL TIME" : w.toUpperCase()}
            </button>
          ))}
        </div>
      </header>

      <ol className="space-y-2">
        {rows.map((r, idx) => (
          <li
            key={r.user_id}
            data-testid={`tips-lb-row-${idx + 1}`}
            className="flex items-center gap-3 p-2.5 rounded-xl bg-black/30 border border-white/5"
          >
            <span
              className={`text-2xl font-black w-8 text-center ${rankColor(idx)}`}
            >
              {idx + 1}
            </span>
            <AvatarChip user={r} size={36} />
            <div className="flex-1 min-w-0">
              <p className="font-bold text-white text-sm truncate">
                {r.name || r.user_id}
              </p>
              <p className="text-[10px] text-white/40 tracking-widest">
                {r.tips_count} {r.tips_count === 1 ? "TIP" : "TIPS"}
              </p>
            </div>
            <p className="text-[#14b8a6] font-black text-lg">
              ${Number(r.tips_total_usd).toFixed(2)}
            </p>
          </li>
        ))}
      </ol>
    </section>
  );
}

function rankColor(idx) {
  // Gold / silver / bronze for the top 3; everyone else just light grey.
  if (idx === 0) return "text-amber-300";
  if (idx === 1) return "text-slate-200";
  if (idx === 2) return "text-orange-400";
  return "text-white/40";
}
